import { useState } from 'react';
import { MapPin, Navigation, Loader2, Check, X } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

/**
 * GPS Location Picker Component
 * Allows users to set GPS coordinates for a target (chantier, client, prospect)
 * 
 * @param {string} targetType - 'chantier' | 'client' | 'prospect'
 * @param {string} targetId - The ID of the target
 * @param {number} currentLatitude - Current latitude value (optional)
 * @param {number} currentLongitude - Current longitude value (optional)
 * @param {function} onUpdate - Callback when coordinates are updated
 */
export function GPSLocationPicker({ 
  targetType, 
  targetId, 
  currentLatitude, 
  currentLongitude, 
  onUpdate,
  compact = false 
}) {
  const [loading, setLoading] = useState(false);
  const [latitude, setLatitude] = useState(currentLatitude || '');
  const [longitude, setLongitude] = useState(currentLongitude || '');
  const [showManual, setShowManual] = useState(false);

  const hasCoordinates = currentLatitude && currentLongitude;

  // Get current position from browser
  const getCurrentPosition = () => {
    if (!navigator.geolocation) {
      toast.error("La géolocalisation n'est pas supportée");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Save to backend
        await saveCoordinates(lat, lng);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Autorise l'accès à ta position");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Position indisponible");
            break;
          default:
            toast.error("Erreur de géolocalisation");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Save coordinates to backend
  const saveCoordinates = async (lat, lng) => {
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/pointage/coordinates/${targetType}/${targetId}?latitude=${lat}&longitude=${lng}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Position GPS enregistrée');
        setLatitude(lat);
        setLongitude(lng);
        if (onUpdate) {
          onUpdate({ latitude: lat, longitude: lng });
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Save manual coordinates
  const saveManualCoordinates = async () => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Coordonnées invalides');
      return;
    }

    if (lat < -90 || lat > 90) {
      toast.error('Latitude doit être entre -90 et 90');
      return;
    }

    if (lng < -180 || lng > 180) {
      toast.error('Longitude doit être entre -180 et 180');
      return;
    }

    setLoading(true);
    await saveCoordinates(lat, lng);
    setLoading(false);
    setShowManual(false);
  };

  // Open in Google Maps
  const openInMaps = () => {
    if (hasCoordinates) {
      window.open(`https://www.google.com/maps?q=${currentLatitude},${currentLongitude}`, '_blank');
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {hasCoordinates ? (
          <>
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Check className="w-3 h-3" />
              GPS
            </span>
            <button
              onClick={openInMaps}
              className="text-xs text-blue-600 hover:underline"
            >
              Voir
            </button>
          </>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={getCurrentPosition}
            disabled={loading}
            className="text-xs h-7 px-2"
          >
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <MapPin className="w-3 h-3 mr-1" />
                GPS
              </>
            )}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="gps-location-picker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Position GPS</span>
        </div>
        {hasCoordinates && (
          <span className="text-xs text-green-600 flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full">
            <Check className="w-3 h-3" />
            Configurée
          </span>
        )}
      </div>

      {hasCoordinates ? (
        <div className="space-y-3">
          <div className="text-xs text-slate-500 font-mono bg-white px-3 py-2 rounded border border-slate-200">
            {currentLatitude.toFixed(6)}, {currentLongitude.toFixed(6)}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openInMaps}
              className="flex-1"
            >
              Voir sur la carte
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={getCurrentPosition}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Navigation className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Définissez la position GPS pour activer le pointage automatique.
          </p>
          
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={getCurrentPosition}
              disabled={loading}
              className="flex-1"
              data-testid="gps-auto-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Navigation className="w-4 h-4 mr-2" />
              )}
              Position actuelle
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowManual(!showManual)}
            >
              Manuel
            </Button>
          </div>

          {showManual && (
            <div className="space-y-2 p-3 bg-white rounded border border-slate-200">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    placeholder="48.8566"
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    placeholder="2.3522"
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowManual(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={saveManualCoordinates}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GPSLocationPicker;
